import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Persist a piece of state to localStorage, hydrating from disk on mount.
 * Gracefully handles SSR and unavailable storage.
 *
 * `initialValue` is treated like React's `useState` initialiser — it is only
 * evaluated when no stored value exists and is captured in a ref on first
 * render.  Passing an inline arrow function is therefore safe and will not
 * cause an infinite re-render loop.
 */
export function useLocalStorageState<T>(
  key: string,
  initialValue: T | (() => T),
): [T, (value: T | ((prev: T) => T)) => void, () => void] {
  // Capture initialValue once so an inline function reference passed by the
  // caller does not appear as a changed dependency on every render.
  const initialValueRef = useRef(initialValue);

  const readValue = useCallback((): T => {
    const init = initialValueRef.current;
    const fallback = (): T => (init instanceof Function ? (init as () => T)() : init);

    if (typeof window === 'undefined') return fallback();
    try {
      const raw = window.localStorage.getItem(key);
      return raw === null ? fallback() : (JSON.parse(raw) as T);
    } catch {
      return fallback();
    }
  }, [key]); // initialValue intentionally omitted — read from ref instead

  const [stored, setStored] = useState<T>(readValue);
  const keyRef = useRef(key);
  keyRef.current = key;

  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      setStored((prev) => {
        const next =
          value instanceof Function ? (value as (p: T) => T)(prev) : value;
        try {
          if (typeof window !== 'undefined') {
            window.localStorage.setItem(keyRef.current, JSON.stringify(next));
          }
        } catch {
          // storage full / unavailable — soldier on
        }
        return next;
      });
    },
    [],
  );

  const clear = useCallback(() => {
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(keyRef.current);
      }
    } catch {
      // ignore
    }
  }, []);

  // Re-hydrate when key changes (readValue is now stable unless key changes)
  useEffect(() => {
    setStored(readValue());
  }, [readValue]);

  return [stored, setValue, clear];
}
